import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  listTelegramDeliveries,
  retryTelegramDeliveriesNow,
  retryTelegramDeliveryById,
} from "@/lib/telegram-log.functions";

export const Route = createFileRoute("/_authenticated/telegram-log")({
  component: TelegramLogPage,
  head: () => ({ meta: [{ title: "Telegram-Log — InboxPulse" }] }),
});

type Row = {
  id: string;
  created_at: string;
  updated_at: string;
  chat_id: string | null;
  title: string;
  message: string;
  parse_mode: string | null;
  text_payload: string | null;
  status: "pending" | "sent" | "failed" | "giving_up" | "skipped";
  attempts: number;
  max_attempts: number;
  last_error: string | null;
  last_attempt_at: string | null;
  next_retry_at: string | null;
  source: string | null;
  ids: Record<string, string> | null;
  links: Array<{ label: string; url: string }> | null;
  telegram_message_id: number | null;
};

function statusBadge(s: Row["status"]) {
  switch (s) {
    case "sent":
      return <Badge className="bg-emerald-600 hover:bg-emerald-600">Gesendet</Badge>;
    case "failed":
      return <Badge variant="destructive">Fehler · Retry geplant</Badge>;
    case "giving_up":
      return <Badge variant="destructive">Aufgegeben</Badge>;
    case "pending":
      return <Badge variant="secondary">Pending</Badge>;
    case "skipped":
      return <Badge variant="outline">Übersprungen</Badge>;
  }
}

function TelegramLogPage() {
  const fetchRows = useServerFn(listTelegramDeliveries);
  const retryAll = useServerFn(retryTelegramDeliveriesNow);
  const retryOne = useServerFn(retryTelegramDeliveryById);
  const qc = useQueryClient();
  const [status, setStatus] = useState<Row["status"] | "all">("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Row | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["telegram-log", status, search],
    queryFn: () => fetchRows({ data: { status, search: search || undefined, limit: 200 } }),
    refetchInterval: 15000,
  });

  const retryAllMut = useMutation({
    mutationFn: () => retryAll({ data: { limit: 25 } }),
    onSuccess: (r) => {
      toast.success(`Retry: ${r.sent} gesendet, ${r.failed} weiter offen, ${r.givenUp} aufgegeben`);
      qc.invalidateQueries({ queryKey: ["telegram-log"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const retryOneMut = useMutation({
    mutationFn: (id: string) => retryOne({ data: { id } }),
    onSuccess: (r) => {
      toast.success(r.sent > 0 ? "Erfolgreich erneut gesendet" : "Erneuter Versuch geplant");
      qc.invalidateQueries({ queryKey: ["telegram-log"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = (data?.rows ?? []) as Row[];

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Telegram-Zustell-Log"
        description="Audit-Log aller Telegram-Benachrichtigungen mit automatischem Retry bei Fehlern. Klick auf eine Zeile zeigt den vollständigen Payload."
      />

      <HelpPanel
        title="Hilfe: Wurden meine Telegram-Nachrichten zugestellt?"
        what={
          <>
            Logbuch aller Telegram-Pushes, die das System verschickt hat. Mit Status (zugestellt /
            Fehler / Wiederholung).
          </>
        }
        why={
          <>
            Falls ein Alarm nicht angekommen ist: hier siehst du, ob das System ihn gesendet hat
            oder ob Telegram ihn abgelehnt hat.
          </>
        }
        how={[
          <>Liste durchsehen, rote Einträge = Fehler.</>,
          <>Auf eine Zeile klicken: kompletter Inhalt + Telegram-Antwort.</>,
        ]}
        tips={[
          <>"Chat not found" → Chat-ID ist falsch oder der Bot wurde aus dem Chat geworfen.</>,
        ]}
        glossary={[
          { term: "Payload", def: "Der eigentliche Inhalt, der an Telegram geschickt wird." },
          { term: "Retry", def: "Erneuter Versuch nach einem Fehler." },
        ]}
      />

      <Card className="p-4 bg-card/60 border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="sent">Gesendet</SelectItem>
                <SelectItem value="failed">Fehler (Retry geplant)</SelectItem>
                <SelectItem value="giving_up">Aufgegeben</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="skipped">Übersprungen</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Suche</Label>
            <Input
              placeholder="Titel, Quelle, Fehler…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => refetch()}>
              Aktualisieren
            </Button>
            <Button onClick={() => retryAllMut.mutate()} disabled={retryAllMut.isPending}>
              {retryAllMut.isPending ? "Sende…" : "Fällige retry"}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-0 bg-card/60 border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left p-3">Zeit</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Titel</th>
                <th className="text-left p-3">Quelle</th>
                <th className="text-left p-3">Versuche</th>
                <th className="text-left p-3">Nächster Retry</th>
                <th className="text-left p-3">Fehler</th>
                <th className="text-right p-3">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-muted-foreground">
                    Lade…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-muted-foreground">
                    Keine Einträge.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-border hover:bg-muted/30 cursor-pointer"
                  onClick={() => setSelected(r)}
                >
                  <td className="p-3 whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="p-3">{statusBadge(r.status)}</td>
                  <td className="p-3 max-w-[280px] truncate" title={r.title}>
                    {r.title}
                  </td>
                  <td className="p-3">{r.source ?? "—"}</td>
                  <td className="p-3 whitespace-nowrap">
                    {r.attempts}/{r.max_attempts}
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    {r.next_retry_at ? new Date(r.next_retry_at).toLocaleString() : "—"}
                  </td>
                  <td
                    className="p-3 max-w-[260px] truncate text-destructive"
                    title={r.last_error ?? ""}
                  >
                    {r.last_error ?? "—"}
                  </td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {(r.status === "failed" || r.status === "giving_up") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => retryOneMut.mutate(r.id)}
                        disabled={retryOneMut.isPending}
                      >
                        Retry
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Telegram-Zustellung</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Status" value={selected.status} />
                <Field label="Versuche" value={`${selected.attempts} / ${selected.max_attempts}`} />
                <Field label="Chat-ID" value={selected.chat_id} mono />
                <Field label="Quelle" value={selected.source} />
                <Field
                  label="Letzter Versuch"
                  value={
                    selected.last_attempt_at
                      ? new Date(selected.last_attempt_at).toLocaleString()
                      : null
                  }
                />
                <Field
                  label="Nächster Retry"
                  value={
                    selected.next_retry_at
                      ? new Date(selected.next_retry_at).toLocaleString()
                      : null
                  }
                />
                <Field
                  label="Telegram message_id"
                  value={selected.telegram_message_id?.toString() ?? null}
                />
              </div>
              {selected.last_error && (
                <div>
                  <Label className="text-muted-foreground">Letzter Fehler</Label>
                  <pre className="mt-1 p-3 bg-muted/40 rounded text-xs whitespace-pre-wrap break-words text-destructive">
                    {selected.last_error}
                  </pre>
                </div>
              )}
              <div>
                <Label className="text-muted-foreground">Titel</Label>
                <div className="mt-1">{selected.title}</div>
              </div>
              <div>
                <Label className="text-muted-foreground">Nachricht</Label>
                <pre className="mt-1 p-3 bg-muted/40 rounded text-xs whitespace-pre-wrap break-words">
                  {selected.message}
                </pre>
              </div>
              {selected.ids && Object.keys(selected.ids).length > 0 && (
                <div>
                  <Label className="text-muted-foreground">IDs</Label>
                  <pre className="mt-1 p-3 bg-muted/40 rounded text-xs whitespace-pre-wrap break-words">
                    {JSON.stringify(selected.ids, null, 2)}
                  </pre>
                </div>
              )}
              {selected.links && selected.links.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Admin-Links</Label>
                  <ul className="mt-1 space-y-1">
                    {selected.links.map((l) => (
                      <li key={l.url}>
                        <a
                          href={l.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary underline"
                        >
                          {l.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.text_payload && (
                <div>
                  <Label className="text-muted-foreground">
                    Telegram-Payload ({selected.parse_mode ?? "plain"})
                  </Label>
                  <pre className="mt-1 p-3 bg-muted/40 rounded text-xs whitespace-pre-wrap break-words max-h-72 overflow-auto">
                    {selected.text_payload}
                  </pre>
                </div>
              )}
              <div className="flex justify-between">
                {selected.status === "failed" || selected.status === "giving_up" ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      retryOneMut.mutate(selected.id);
                      setSelected(null);
                    }}
                  >
                    Erneut senden
                  </Button>
                ) : (
                  <span />
                )}
                <Button variant="outline" onClick={() => setSelected(null)}>
                  Schließen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div>
      <Label className="text-muted-foreground">{label}</Label>
      <div className={mono ? "font-mono text-xs break-all" : "text-sm"}>{value ?? "—"}</div>
    </div>
  );
}
